import React, { FC, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { styled } from '@material-ui/core';
import apiRoutes from '../../../config/api/routes';
import { loginMapper } from '../../../config/api/models';
import { LoginRequest } from '../../../config/api/models/Login';
import { useFetch } from '../../shared';
import {
  Button,
  CheckboxAdapter,
  FormControl,
  Message,
  Icons,
  LinkButton,
  TextFieldAdapter,
  validations,
} from '../../shared/components';
import { useHistory } from 'react-router';
import { useI18nPath } from '../../../config/clientRoutes/hooks';
import { useSearchParam } from 'react-use';
import { useAppDispatch, useAppSelector } from '../../shared/hooks/useAppDispatch';
import { setJwt, setUser } from '../../shared/slices/auth/entity/AuthSlice';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';

const Wrapper = styled('div')({
  marginBottom: '4rem',
});

const LoginForm: FC = () => {
  const log = useDataLogging();
  const [loginResponseV2, loginV2] = useFetch(apiRoutes.loginV2(), {
    method: 'POST',
    mapper: loginMapper,
  });
  const [rememberMe, setRememberMe] = useState(false);
  const history = useHistory();
  const { getPath } = useI18nPath();
  const { t } = useTranslation();
  const idbError = useSearchParam('idb_error');
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  //It'll handle the impersonation in case the impersonated user haven't accepted the privacy terms
  useEffect(() => {
    if (user && !user.privacyAcceptedAt) {
      history.push(getPath('cookieCompliance'));
    }
  }, [user, history, getPath]);

  //It'll handle the form authentication
  useEffect(() => {
    if (loginResponseV2.success) {
      dispatch(setJwt(loginResponseV2.data.token));
      log(LogActionTypes.USER_LOGIN);

      if (!loginResponseV2.data.user.state?.isClient && loginResponseV2.data.user.state?.token) {
        const absoluteURL = new URL(
          `${loginResponseV2.data.user.state?.redirectTo}?token=${loginResponseV2.data.user.state?.token}`,
          window.location.origin
        ).href;
        setTimeout(() => {
          window.location.href = absoluteURL;
        }, 0);
      }
      dispatch(setUser(loginResponseV2.data.user));

      if (!loginResponseV2.data.user.privacyAcceptedAt) history.push(getPath('cookieCompliance'));
    }
  }, [loginResponseV2, rememberMe, history, getPath, dispatch, log]);

  return (
    <Wrapper>
      <Form
        onSubmit={(values: LoginRequest) => {
          setRememberMe(values.remember_me);
          loginV2({ body: values });
        }}
        render={({ handleSubmit, valid }) => (
          <form onSubmit={handleSubmit}>
            <FormControl label={t('shared.forms.email')} id="email" required>
              <Field
                name="email"
                component={TextFieldAdapter}
                validate={validations(t).isEmail()}
                type="email"
              />
            </FormControl>
            <FormControl label={t('shared.forms.password')} id="password" required>
              <Field
                name="password"
                component={TextFieldAdapter}
                validate={validations(t).isValidPassWord}
                type="password"
              />
            </FormControl>
            <FormControl id="remember_me">
              <Field
                name="remember_me"
                component={CheckboxAdapter}
                type="checkbox"
                label={t('security.login.rememberMe')}
              />
            </FormControl>
            <>
              <Button type="submit" icon={Icons.SUBMIT} disabled={!valid}>
                {t('security.login.loginBtn')}
              </Button>
              <LinkButton to="forgotPassword">{t('security.login.forgotPasswordBtn')}</LinkButton>
            </>
            {loginResponseV2 && loginResponseV2.error && (
              <Message type="error">{t(`shared.errors.loginInvalidCredentials`)}</Message>
            )}
            {idbError !== null && <Message type="error">{t(`shared.errors.generic`)}</Message>}
          </form>
        )}
      />
    </Wrapper>
  );
};

export default LoginForm;
